import $ from 'jquery';
import makeDebugger from './util/devLog';

export type ReactiveAction = 'create' | 'update' | 'destroy';

export type Change = {
  path: string;
  action: ReactiveAction;
  meta?: { [key: string]: any };
};

export type ReactiveCallback = (
  change: Change,
  ownPageChanges: boolean,
  ownComponentChanges: boolean,
  messageClientId?: string
) => void;

type BatchedReactiveCallback = (
  change: Change[],
  ownPageChanges: boolean,
  ownComponentChanges: boolean,
  messageClientId?: string
) => void;

type CallbackEntry = {
  id: string;
  patterns: string[];
  callback: ReactiveCallback;
};

type BatchedCallbackEntry = {
  id: string;
  patterns: string[];
  callback: BatchedReactiveCallback;
};

const debug = makeDebugger('reactive-register');

// Callbacks for single events.
let registered: Record<string, CallbackEntry> = {};

// Callbacks for a batch of similar events.
let batchRegistered: Record<string, BatchedCallbackEntry> = {};

/*
 * Register a callback to receive reactive updates.
 */
function register(
  id: string,
  patterns: string | string[],
  callback: ReactiveCallback
) {
  if (!Array.isArray(patterns)) {
    patterns = [patterns];
  }
  if (isRegistered(id)) {
    console.warn(`Overriding previously registered reactive callback: ${id}`);
  }
  registered[id] = { id, patterns, callback };
}

/*
 * Register a callback to receive batched reactive updates.
 */
function registerBatchedChanges(
  id: string,
  patterns: string | string[],
  callback: BatchedReactiveCallback
) {
  if (!Array.isArray(patterns)) {
    patterns = [patterns];
  }
  if (isRegistered(id)) {
    console.warn(`Overriding previously registered reactive callback: ${id}`);
  }
  batchRegistered[id] = { id, patterns, callback };
}

function deregister(id: string) {
  delete registered[id];
  delete batchRegistered[id];
}

function isRegistered(id: string) {
  return id in registered || id in batchRegistered;
}

function dispatch(
  change: Change,
  ownPageChanges: boolean,
  messageClientId: string
) {
  debug('Dispatching change:', change, ownPageChanges, messageClientId);

  for (const { id, patterns, callback } of Object.values(registered)) {
    if (patterns.find(pattern => change.path.includes(pattern))) {
      const ccid = componentClientId(id);
      const ownComponentChanges = messageClientId === ccid;

      callback(change, ownPageChanges, ownComponentChanges, messageClientId);
    }
  }
}

function dispatchBatch(
  changes: Change[],
  ownPageChanges: boolean,
  messageClientId: string
) {
  debug('Dispatching batch change:', changes, ownPageChanges, messageClientId);

  for (const { id, patterns, callback } of Object.values(batchRegistered)) {
    // Collect all matching changes in the batch.
    const matchingChanges = changes.filter(change =>
      patterns.find(pattern => change.path.includes(pattern))
    );

    if (matchingChanges.length > 0) {
      const ccid = componentClientId(id);
      const ownComponentChanges = messageClientId === ccid;

      callback(
        matchingChanges,
        ownPageChanges,
        ownComponentChanges,
        messageClientId
      );
    }
  }
}

function componentClientId(id: string) {
  return `${window.reactiveClientId ? window.reactiveClientId() : null}-${id}`;
}

// Don't hold on to callbacks through switching pages (eg with turbolinks)
$(document).on('page:change', () => {
  registered = {};
  batchRegistered = {};
});

export default {
  register,
  registerBatchedChanges,
  deregister,
  isRegistered,
  dispatch,
  dispatchBatch,
  componentClientId,
};
